import React, { useState } from 'react';
import {
  Tag,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  AlertTriangle,
  RotateCcw,
  Package,
} from 'lucide-react';
import { InventoryItem } from '../types';
import { defaultInventoryCategories } from '../data/initialData';

interface ManageInventoryCategoriesModalProps {
  isOpen: boolean;
  categories: string[];
  inventory: InventoryItem[];
  onClose: () => void;
  onSaveCategories: (categories: string[]) => void;
  onUpdateInventory: (items: InventoryItem[]) => void;
}

export const ManageInventoryCategoriesModal: React.FC<ManageInventoryCategoriesModalProps> = ({
  isOpen,
  categories,
  inventory,
  onClose,
  onSaveCategories,
  onUpdateInventory,
}) => {
  const [newCatInput, setNewCatInput] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen) return null;

  // Count items per category
  const getItemCount = (catName: string) => {
    return inventory.filter(item => (item.category || '').trim().toLowerCase() === catName.trim().toLowerCase()).length;
  };

  const handleAddCategory = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const trimmed = newCatInput.trim();
    if (!trimmed) {
      setErrorMessage('Lütfen eklenecek kategori adını yazınız.');
      return;
    }

    const exists = categories.some(c => c.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setErrorMessage('Bu kategori adı zaten mevcut.');
      return;
    }

    const updated = [...categories, trimmed];
    onSaveCategories(updated);
    setNewCatInput('');
    setSuccessMessage(`"${trimmed}" kategorisi başarıyla eklendi.`);
    setTimeout(() => setSuccessMessage(''), 2500);
  };

  const handleStartEdit = (index: number, currentVal: string) => {
    setEditingIndex(index);
    setEditingValue(currentVal);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleSaveEdit = (index: number) => {
    const trimmed = editingValue.trim();
    const oldName = categories[index];

    if (!trimmed) {
      setErrorMessage('Kategori adı boş bırakılamaz.');
      return;
    }

    if (trimmed.toLowerCase() !== oldName.toLowerCase()) {
      const exists = categories.some((c, i) => i !== index && c.toLowerCase() === trimmed.toLowerCase());
      if (exists) {
        setErrorMessage('Bu kategori adı başka bir kayıtta zaten kullanılıyor.');
        return;
      }
    }

    const updatedCategories = [...categories];
    updatedCategories[index] = trimmed;
    onSaveCategories(updatedCategories);

    // If there are inventory items with old category name, update them
    if (oldName !== trimmed) {
      const affectedItems = inventory.filter(
        item => (item.category || '').trim().toLowerCase() === oldName.trim().toLowerCase()
      );
      if (affectedItems.length > 0) {
        const updatedInventory = inventory.map(item => {
          if ((item.category || '').trim().toLowerCase() === oldName.trim().toLowerCase()) {
            return { ...item, category: trimmed, updatedAt: new Date().toISOString() };
          }
          return item;
        });
        onUpdateInventory(updatedInventory);
      }
    }

    setEditingIndex(null);
    setEditingValue('');
    setSuccessMessage(`Kategori adı "${trimmed}" olarak güncellendi.`);
    setTimeout(() => setSuccessMessage(''), 2500);
  };

  const handleDeleteCategory = (catName: string) => {
    setErrorMessage('');
    setSuccessMessage('');

    const count = getItemCount(catName);
    if (count > 0) {
      const confirmMsg = `"${catName}" kategorisine ait ${count} adet stok ürünü bulunuyor.\n\nBu kategoriyi silerseniz, bu ${count} adet ürünün kategorisi "Diğer" olarak güncellenecektir.\n\nSilmek istediğinize emin misiniz?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      // Update inventory items to "Diğer"
      const updatedInventory = inventory.map(item => {
        if ((item.category || '').trim().toLowerCase() === catName.trim().toLowerCase()) {
          return { ...item, category: 'Diğer', updatedAt: new Date().toISOString() };
        }
        return item;
      });
      onUpdateInventory(updatedInventory);
    } else {
      if (!window.confirm(`"${catName}" kategorisini silmek istediğinize emin misiniz?`)) {
        return;
      }
    }

    let updatedCategories = categories.filter(c => c.toLowerCase() !== catName.toLowerCase());
    if (updatedCategories.length === 0) {
      updatedCategories = ['Diğer'];
    } else if (count > 0 && !updatedCategories.some(c => c.toLowerCase() === 'diğer')) {
      updatedCategories.push('Diğer');
    }

    onSaveCategories(updatedCategories);
    setSuccessMessage(`"${catName}" kategorisi silindi.`);
    setTimeout(() => setSuccessMessage(''), 2500);
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Kategorileri varsayılan listeye sıfırlamak istediğinize emin misiniz?')) {
      onSaveCategories(defaultInventoryCategories);
      setSuccessMessage('Kategoriler varsayılan listeye sıfırlandı.');
      setTimeout(() => setSuccessMessage(''), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl sm:max-w-3xl w-full max-h-[90vh] flex flex-col border border-slate-200 overflow-hidden my-auto text-left">
        {/* Header */}
        <div className="p-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-xs">
              <Tag className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm">Stok Kategorilerini Yönet</h3>
              <p className="text-[11px] text-slate-400">
                Parça ve envanter kategorilerini ekleyin, düzenleyin veya silin ({categories.length} Kategori)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Add New Category Form */}
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <form onSubmit={handleAddCategory} className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Yeni kategori adı yazın (Örn: Anakart / Board, Şarj Aleti)..."
                value={newCatInput}
                onChange={e => setNewCatInput(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Kategori Ekle</span>
            </button>
          </form>

          {/* Feedback messages */}
          {errorMessage && (
            <div className="mt-2.5 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-xs font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="mt-2.5 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-xs font-medium flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}
        </div>

        {/* Categories List */}
        <div className="p-4 overflow-y-auto flex-1 space-y-2">
          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider px-1 pb-1">
            <span>Mevcut Kategoriler</span>
            <span>Bağlı Ürün</span>
          </div>

          <div className="space-y-1.5">
            {categories.map((cat, index) => {
              const count = getItemCount(cat);
              const isEditing = editingIndex === index;

              return (
                <div
                  key={`${cat}-${index}`}
                  className="flex items-center justify-between p-2.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl transition-all group"
                >
                  {isEditing ? (
                    <div className="flex items-center gap-2 flex-1 mr-2">
                      <input
                        type="text"
                        autoFocus
                        value={editingValue}
                        onChange={e => setEditingValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveEdit(index);
                          if (e.key === 'Escape') setEditingIndex(null);
                        }}
                        className="w-full px-2.5 py-1.5 text-xs font-bold border border-indigo-400 rounded-lg bg-indigo-50/40 text-slate-900 focus:outline-none"
                      />
                      <button
                        onClick={() => handleSaveEdit(index)}
                        className="p-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        title="Kaydet"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        title="İptal"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                        <div className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                        <span className="text-xs font-bold text-slate-800 truncate">{cat}</span>
                      </div>

                      <div className="flex items-center space-x-2 shrink-0">
                        <span
                          className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-md ${
                            count > 0
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                          title={`${count} adet ürün bu kategoriye ait`}
                        >
                          <span className="flex items-center gap-1">
                            <Package className="w-3 h-3 inline" />
                            {count}
                          </span>
                        </span>

                        <button
                          onClick={() => handleStartEdit(index, cat)}
                          className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                          title="Kategori Adını Düzenle"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => handleDeleteCategory(cat)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          title="Kategoriyi Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleResetToDefaults}
            className="text-xs font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1.5 transition-colors cursor-pointer"
            title="Varsayılan standart kategori listesini geri yükler"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Varsayılanlara Sıfırla</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition-all cursor-pointer"
          >
            Kapat
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
